import { cs } from '../../lib/currency';
/**
 * iter334cj — Super Admin: Tenant Billing Profile + Discount Config
 *
 * Rendered under Super Admin → Tenant Detail → Billing tab. Reuses
 * the existing `PUT /api/super-admin/tenants/{id}/billing-profile`
 * endpoint (iter334cg) to configure:
 *
 *   • Plan id (dropdown of available plans)
 *   • Monthly price override
 *   • Discount config — matches the DiscountConfig shape from the
 *     enrollment/coaching side (kind: percent|flat|none, amount,
 *     applies_from, applies_until). Supports both time-boxed and
 *     permanent discounts.
 *   • Billing status (active / suspended / cancelled)
 *   • Manual "Preview Next Invoice" and "Charge Now" actions.
 *
 * Does NOT reimplement discount math — the backend
 * `_compute_effective_price` already handles it identically to how
 * enrollment discounts are computed via `calculate_discount_amount`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, DollarSign, CalendarClock, Percent, Play, Eye } from "lucide-react";
import SaasCardManager from "../../components/billing/SaasCardManager";
import BillingCurrencyCard from "./BillingCurrencyCard";

const API = (typeof window !== "undefined" && window.location
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminTenantBillingTab({ tenantId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingCurrency, setBillingCurrency] = useState("usd");
  const [profile, setProfile] = useState({
    plan_id: "starter",
    monthly_price: 0,
    status: "active",
    billing_email: "",
    discount: {
      kind: "none",
      amount: 0,
      applies_from: "",
      applies_until: "",
      reason: "",
    },
  });

  const load = async () => {
    setLoading(true);
    try {
      const [profileRes, plansRes] = await Promise.all([
        axios.get(`${API}/super-admin/tenants/${tenantId}/billing-profile`, { headers: authHeader() }),
        axios.get(`${API}/super-admin/plans`, { headers: authHeader() }).catch(() => ({ data: [] })),
      ]);
      setPlans(plansRes.data?.plans || plansRes.data || []);
      const p = profileRes.data;
      if (p && !p.exists && p.tenant_id) {
        // Empty profile — keep defaults, just stamp the tenant id.
        setProfile((prev) => ({ ...prev, tenant_id: tenantId }));
      } else if (p) {
        setBillingCurrency(p.billing_currency || "usd");
        setProfile({
          plan_id: p.plan_id || "starter",
          monthly_price: Number(p.monthly_price) || 0,
          status: p.status || "active",
          billing_email: p.billing_email || "",
          discount: p.discount || {
            kind: "none", amount: 0, applies_from: "", applies_until: "", reason: "",
          },
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load billing profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) load();
  }, [tenantId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        tenant_id: tenantId,
        plan_id: profile.plan_id,
        monthly_price: Number(profile.monthly_price),
        status: profile.status,
        billing_email: profile.billing_email || null,
        discount: profile.discount.kind === "none"
          ? null
          : {
              ...profile.discount,
              amount: Number(profile.discount.amount || 0),
              applies_from: profile.discount.applies_from || null,
              applies_until: profile.discount.applies_until || null,
            },
      };
      await axios.put(`${API}/super-admin/tenants/${tenantId}/billing-profile`, body, {
        headers: authHeader(),
      });
      toast.success("Billing profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const previewInvoice = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API}/super-admin/tenants/${tenantId}/preview-invoice`,
        {},
        { headers: authHeader() },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to preview");
    } finally {
      setPreviewing(false);
    }
  };

  const setDisc = (k, v) => setProfile((p) => ({
    ...p,
    discount: { ...p.discount, [k]: v },
  }));

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading billing profile…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-tenant-billing-tab">
      <BillingCurrencyCard tenantId={tenantId} current={billingCurrency} planAmountUsd={Number(profile.monthly_price) || 0} />
      {/* ---------- Plan + Price ---------- */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-blue-600" /> Plan &amp; Pricing
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Plan</span>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              value={profile.plan_id}
              onChange={(e) => setProfile((p) => ({ ...p, plan_id: e.target.value }))}
              data-testid="tenant-billing-plan"
            >
              {(plans.length ? plans : [{ id: "lane_only", name: "Lane Bookings Only" },
                                        { id: "starter", name: "Starter" },
                                        { id: "professional", name: "Professional" },
                                        { id: "enterprise", name: "Enterprise" }]).map((pl) => (
                <option key={pl.id} value={pl.id}>{pl.name || pl.id}</option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Monthly Price ($)</span>
            <input
              type="number" min="0" step="0.01"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              value={profile.monthly_price}
              onChange={(e) => setProfile((p) => ({ ...p, monthly_price: e.target.value }))}
              data-testid="tenant-billing-monthly-price"
            />
          </label>
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Status</span>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              value={profile.status}
              onChange={(e) => setProfile((p) => ({ ...p, status: e.target.value }))}
              data-testid="tenant-billing-status"
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Billing Email (for receipts)</span>
            <input
              type="email"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="admin@tenant.com"
              value={profile.billing_email}
              onChange={(e) => setProfile((p) => ({ ...p, billing_email: e.target.value }))}
              data-testid="tenant-billing-email"
            />
          </label>
        </div>
      </div>

      {/* ---------- Cards on file (onboarding) ---------- */}
      <SaasCardManager
        basePath={`/super-admin/tenants/${tenantId}`}
        title="Cards on File (SaaS billing)"
      />

      {/* ---------- Data-drift findings for this tenant ---------- */}

      {/* ---------- Discount ---------- */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-5 space-y-4">
        <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
          <Percent className="h-5 w-5" /> Discount
        </h3>
        <p className="text-xs text-emerald-800">
          Applies to the monthly SaaS charge. Same shape as tenant-side enrollment discounts (flat $ or %, time-boxed or permanent).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-xs">
            <span className="block text-emerald-900 mb-1">Kind</span>
            <select
              className="w-full rounded border border-emerald-300 px-2 py-1.5 text-sm"
              value={profile.discount.kind}
              onChange={(e) => setDisc("kind", e.target.value)}
              data-testid="tenant-billing-discount-kind"
            >
              <option value="none">No discount</option>
              <option value="percent">Percent off</option>
              <option value="flat">Flat $ off</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="block text-emerald-900 mb-1">
              Amount {profile.discount.kind === "percent" ? "(%)" : "($)"}
            </span>
            <input
              type="number" min="0" step="0.01"
              className="w-full rounded border border-emerald-300 px-2 py-1.5 text-sm"
              value={profile.discount.amount}
              disabled={profile.discount.kind === "none"}
              onChange={(e) => setDisc("amount", e.target.value)}
              data-testid="tenant-billing-discount-amount"
            />
          </label>
          <label className="text-xs">
            <span className="block text-emerald-900 mb-1">Reason (internal)</span>
            <input
              type="text"
              className="w-full rounded border border-emerald-300 px-2 py-1.5 text-sm"
              placeholder="e.g. Early customer, 3mo trial"
              value={profile.discount.reason}
              disabled={profile.discount.kind === "none"}
              onChange={(e) => setDisc("reason", e.target.value)}
              data-testid="tenant-billing-discount-reason"
            />
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs">
            <span className="block text-emerald-900 mb-1 flex items-center gap-1">
              <CalendarClock className="h-3 w-3" /> Applies From (blank = immediately)
            </span>
            <input
              type="date"
              className="w-full rounded border border-emerald-300 px-2 py-1.5 text-sm"
              value={profile.discount.applies_from ? profile.discount.applies_from.slice(0, 10) : ""}
              disabled={profile.discount.kind === "none"}
              onChange={(e) => setDisc("applies_from", e.target.value)}
              data-testid="tenant-billing-discount-from"
            />
          </label>
          <label className="text-xs">
            <span className="block text-emerald-900 mb-1 flex items-center gap-1">
              <CalendarClock className="h-3 w-3" /> Applies Until (blank = permanent)
            </span>
            <input
              type="date"
              className="w-full rounded border border-emerald-300 px-2 py-1.5 text-sm"
              value={profile.discount.applies_until ? profile.discount.applies_until.slice(0, 10) : ""}
              disabled={profile.discount.kind === "none"}
              onChange={(e) => setDisc("applies_until", e.target.value)}
              data-testid="tenant-billing-discount-until"
            />
          </label>
        </div>
      </div>

      {/* ---------- Actions ---------- */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          data-testid="tenant-billing-save"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Billing Profile
        </button>
        <button
          onClick={previewInvoice}
          disabled={previewing}
          className="px-4 py-2 rounded border border-slate-300 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
          data-testid="tenant-billing-preview"
        >
          {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          Preview Next Invoice
        </button>
      </div>

      {/* ---------- Preview Result ---------- */}
      {preview && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="tenant-billing-preview-result">
          <p className="font-semibold text-slate-900 mb-2">Next Invoice Preview{preview.evaluated_as_of ? ` — for billing on ${String(preview.evaluated_as_of).slice(0, 10)}` : ""}</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-slate-500">Base:</span> {cs()}{preview.base_price?.toFixed(2)}</div>
            <div><span className="text-slate-500">Effective:</span> <strong>{cs()}{preview.effective_price?.toFixed(2)}</strong></div>
            <div className="col-span-2">
              <span className="text-slate-500">Discount:</span>{" "}
              {preview.discount?.applied ? (
                <span className="text-emerald-700">Applied — {preview.discount.kind === "percent"
                  ? `${preview.discount.amount}% (−${cs()}${preview.discount.discount_value})`
                  : `−${cs()}${preview.discount.discount_value}`}
                </span>
              ) : (
                <span className="text-amber-700">
                  {preview.discount?.reason === "not_started"
                    ? `Scheduled — starts ${(preview.discount.applies_from || "").slice(0, 10)} (clear "Applies From" to start immediately)`
                    : preview.discount?.reason === "expired"
                      ? `Expired on ${(preview.discount.applies_until || "").slice(0, 10)}`
                      : `Not applied (${preview.discount?.reason || "no discount"})`}
                </span>
              )}
            </div>
            <div className="col-span-2">
              <span className="text-slate-500">Payment method on file:</span>{" "}
              {preview.has_payment_method
                ? <span className="text-emerald-700">Yes</span>
                : <span className="text-red-600">No — must attach card before next bill</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
